import qrcode from "qrcode-generator";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Sheet } from "./Sheet";

// Kinmantle never sends anyone off to a web page: on the wall there's no
// address bar or back button, so there'd be no way home. A link to the
// outside world opens in the app instead, as a QR code to scan with a phone
// and the address to read.

interface Outside {
  href: string;
  title?: string;
}

const EVENT = "km:outside-link";

export function openOutside(href: string, title?: string) {
  window.dispatchEvent(new CustomEvent<Outside>(EVENT, { detail: { href, title } }));
}

/** A button that looks like a link and opens the "scan to open on your phone" sheet. */
export function OutsideLink({ href, title, className, ariaLabel, children }: { href: string; title?: string; className?: string; ariaLabel?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      className={className}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        openOutside(href, title);
      }}
    >
      {children}
    </button>
  );
}

/**
 * Catches every way of leaving: links (including ones drawn by map libraries
 * or calendar descriptions), new-tab and middle clicks, dragged links and
 * window.open. Same-site pages still open, always in this tab.
 */
export function installLinkGuard() {
  const onClick = (e: MouseEvent) => {
    const a = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>("a[href]") : null;
    if (!a) return;
    const url = new URL(a.href, location.href);
    if (url.origin === location.origin) {
      if ((a.target && a.target !== "_self") || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) {
        e.preventDefault();
        if (e.type === "click" || e.button === 1) location.assign(url.href);
      }
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "click") openOutside(url.href, a.textContent?.trim() || undefined);
  };
  document.addEventListener("click", onClick, true);
  document.addEventListener("auxclick", onClick, true);
  document.addEventListener("dragstart", (e) => {
    if (e.target instanceof Element && e.target.closest("a[href]")) e.preventDefault();
  }, true);
  window.open = (url?: string | URL) => {
    if (url) openOutside(new URL(url, location.href).href);
    return null;
  };
}

export function OutsideLinkHost() {
  const [link, setLink] = useState<Outside | null>(null);
  useEffect(() => {
    const on = (e: Event) => setLink((e as CustomEvent<Outside>).detail);
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  if (!link) return null;

  const close = () => setLink(null);
  const shown = link.href.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
  return (
    <Sheet
      title={link.title ?? "Open on your phone"}
      onClose={close}
      size="sm"
      footer={
        <button className="btn btn-primary" onClick={close}>
          Done
        </button>
      }
    >
      <p className="muted">This page is outside Kinmantle. Point your phone’s camera at the code to open it there.</p>
      <QrCode text={link.href} />
      <p className="outside-url">{shown}</p>
    </Sheet>
  );
}

export function QrCode({ text, className = "outside-qr", label = "QR code for this page" }: { text: string; className?: string; label?: string }) {
  const code = useMemo(() => {
    try {
      const qr = qrcode(0, "M");
      qr.addData(text);
      qr.make();
      const n = qr.getModuleCount();
      let d = "";
      for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
      return { size: n + 8, d };
    } catch {
      return null; // too long for a QR code; the address below still shows
    }
  }, [text]);
  if (!code) return null;
  return (
    <svg className={className} viewBox={`0 0 ${code.size} ${code.size}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect width={code.size} height={code.size} fill="#fff" />
      <path d={code.d} fill="#000" />
    </svg>
  );
}
